import { useQuery } from '@apollo/client';
import { GetLatestOversightEffectivenessByControlIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import { extractCmaCompletedAt, isLater } from '@/utils/oversightUtils';

export type OversightEffectivenessLeaf = {
  DesignEffectiveness: number | null;
  PerformanceEffectiveness: number | null;
  OverallEffectiveness: number | null;
  CompletedAt: string | null;
};

export type OversightEffectivenessByControl = Map<
  string,
  OversightEffectivenessLeaf
>;

const EMPTY_MAP: OversightEffectivenessByControl = new Map();

/**
 * Fetches the latest oversight effectiveness ratings (design / performance / overall)
 * per control for the given control Ids, keyed by ControlId.
 *
 * "Latest" is determined by the linked compliance_monitoring_assessment's
 * ActualCompletionDate. The leaf query filters to completed CMAs with a non-null
 * completion date at both the leaf-eligibility level and the inner cmaParent
 * selection — re-applying at the inner level prevents attaching a stale CMA date
 * to an otherwise eligible row.
 *
 * Unlike the risk variant, control_test_second_line_result has a direct
 * ParentControlId column, so each leaf maps to exactly one control. No fan-out
 * gymnastics required.
 *
 * Skips the query when oversight viewing is disabled or no controls are visible.
 */
export const useLatestOversightEffectivenessByControls = (params: {
  controlIds: string[];
  isEnabled: boolean;
}): {
  effectivenessByControl: OversightEffectivenessByControl;
  loading: boolean;
} => {
  const { controlIds, isEnabled } = params;
  const skip = !isEnabled || controlIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightEffectivenessByControlIdsDocument,
    {
      skip,
      variables: { ControlIds: controlIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const effectivenessByControl =
    useMemo<OversightEffectivenessByControl>(() => {
      if (!data) {
        return EMPTY_MAP;
      }

      const latestByControl = new Map<string, OversightEffectivenessLeaf>();

      for (const row of data.control_test_second_line_result) {
        const completedAt = extractCmaCompletedAt(row.cmaParent);
        const existing = latestByControl.get(row.ParentControlId);
        if (!existing || isLater(existing.CompletedAt, completedAt)) {
          latestByControl.set(row.ParentControlId, {
            DesignEffectiveness: row.DesignEffectiveness ?? null,
            PerformanceEffectiveness: row.PerformanceEffectiveness ?? null,
            OverallEffectiveness: row.OverallEffectiveness ?? null,
            CompletedAt: completedAt,
          });
        }
      }

      return latestByControl;
    }, [data]);

  return { effectivenessByControl, loading };
};
